import { createClient } from '@supabase/supabase-js';
import { debug } from './debug';
import type { Database } from '@/integrations/supabase/types';

// Use the integrated Supabase client instead of environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Debug environment variables in production
console.log('Supabase URL exists:', !!supabaseUrl);
console.log('Supabase Key exists:', !!supabaseAnonKey);
console.log('Environment check:', {
  NODE_ENV: import.meta.env.MODE,
  hasUrl: !!supabaseUrl,
  hasKey: !!supabaseAnonKey
});

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Supabase URL and anonymous key must be provided in environment variables.");
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'implicit'
  }
});

export { supabaseUrl, supabaseAnonKey };

export const signIn = async (email: string, password: string) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      console.error('Sign in error:', error);
      return { data: null, error };
    }
    
    return { data, error: null };
  } catch (err) {
    console.error('Unexpected sign in error:', err);
    return { 
      data: null, 
      error: { message: 'Er is een onverwachte fout opgetreden bij het inloggen.' } 
    };
  }
};

export const signInWithGoogle = async () => {
  try {
    // Use environment-aware URL for OAuth redirect
    const { getAppUrl } = await import('./environment');
    const appUrl = getAppUrl();
    const redirectTo = `${appUrl}/auth`;
    
    console.log('OAuth redirect will go to:', redirectTo);
    console.log('App URL:', appUrl);
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectTo,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent'
        }
      }
    });
    
    if (error) {
      console.error('Google sign in error:', error);
      return { data: null, error };
    }
    
    debug.log('Google OAuth initiated successfully');
    return { data, error: null };
  } catch (err) {
    console.error('Unexpected Google sign in error:', err);
    return { 
      data: null, 
      error: { message: 'Er is een onverwachte fout opgetreden bij Google authenticatie.' } 
    };
  }
};

export const signUp = async (email: string, password: string) => {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          email: email // Store email in user metadata
        }
      }
    });
    
    if (error) {
      console.error('Sign up error:', error);
      return { data: null, error };
    }
    
    // Create profile and household after successful signup
    if (data.user) {
      // Create default household first
      const { data: household, error: householdError } = await supabase
        .from('households')
        .insert([
          {
            name: `${email}'s Household`,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
        ])
        .select()
        .single();

      if (householdError) {
        console.error('Household creation error:', householdError);
      }

      // Create profile with household reference
      const { error: profileError } = await supabase
        .from('profiles')
        .insert([
          {
            id: data.user.id,
            email: email,
            household_id: household?.id || null,
            is_admin: true, // First user in household is admin
            role: 'admin',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
        ]);

      if (profileError) {
        console.error('Profile creation error:', profileError);
      }

      // Create household_members entry
      if (household?.id) {
        const { error: memberError } = await supabase
          .from('household_members')
          .insert([
            {
              household_id: household.id,
              user_id: data.user.id,
              role: 'admin',
              joined_at: new Date().toISOString()
            }
          ]);

        if (memberError) {
          console.error('Household member creation error:', memberError);
        }
      }
    }
    
    return { data, error: null };
  } catch (err) {
    console.error('Unexpected sign up error:', err);
    return { 
      data: null, 
      error: { message: 'Er is een onverwachte fout opgetreden bij het registreren.' } 
    };
  }
};

export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    
    if (error) {
      console.error('Sign out error:', error);
      return { error };
    }
    
    return { error: null };
  } catch (err) {
    console.error('Unexpected sign out error:', err);
    return { 
      error: { message: 'Er is een onverwachte fout opgetreden bij het uitloggen.' } 
    };
  }
};

// ===== HOUSEHOLD MANAGEMENT FUNCTIONS =====

// Get current user's household with all members
export const getCurrentHousehold = async () => {
  try {
    const { data: profile, error: profileError } = await getCurrentUserProfile();
    
    if (profileError || !profile?.household_id) {
      return { data: null, error: profileError || { message: 'Geen huishouden gevonden' } };
    }

    // Get household with all members
    const { data: household, error } = await supabase
      .from('households')
      .select(`
        *,
        household_members (
          user_id,
          role,
          joined_at,
          permissions
        )
      `)
      .eq('id', profile.household_id)
      .single();
      
    if (error) {
      console.error('Error fetching household:', error);
      return { data: null, error };
    }
    
    // Now fetch the profiles for each household member
    if (household && household.household_members && household.household_members.length > 0) {
      const userIds = household.household_members.map((member: any) => member.user_id);
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, email, first_name, last_name, avatar_url')
        .in('id', userIds);
        
      if (!profilesError && profiles) {
        // Merge profile data into household members
        household.household_members = household.household_members.map((member: any) => ({
          ...member,
          profiles: profiles.find(p => p.id === member.user_id) || null
        }));
      }
    }

    return { data: household, error: null };
  } catch (err) {
    console.error('Unexpected error fetching household:', err);
    return { 
      data: null, 
      error: { message: 'Er is een fout opgetreden bij het ophalen van huishouden gegevens.' } 
    };
  }
};

// Update household name
export const updateHouseholdName = async (householdId: string, name: string) => {
  try {
    const { data, error } = await supabase
      .from('households')
      .update({
        name: name,
        updated_at: new Date().toISOString()
      })
      .eq('id', householdId)
      .select()
      .single();

    if (error) {
      console.error('Error updating household name:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (err) {
    console.error('Unexpected error updating household name:', err);
    return { 
      data: null, 
      error: { message: 'Er is een fout opgetreden bij het bijwerken van de huishouden naam.' } 
    };
  }
};

// Invite user to household (creates pending invitation)
export const inviteUserToHousehold = async (email: string, role: 'admin' | 'adult' | 'child' = 'adult') => {
  try {
    const { data: profile, error: profileError } = await getCurrentUserProfile();
    
    if (profileError || !profile?.household_id) {
      return { data: null, error: profileError || { message: 'Geen huishouden gevonden' } };
    }

    // Check if user is admin
    if (!profile.is_admin) {
      return { data: null, error: { message: 'Alleen beheerders kunnen gebruikers uitnodigen' } };
    }

    // Check if user already exists and is not in a household
    const { data: existingProfile } = await supabase
      .from('profiles')
      .select('id, email, household_id')
      .eq('email', email)
      .single();

    if (existingProfile) {
      if (existingProfile.household_id) {
        return { data: null, error: { message: 'Deze gebruiker is al lid van een huishouden' } };
      }
      
      // Add existing user to household
      const { data: memberRecord, error: memberError } = await supabase
        .from('household_members')
        .insert([
          {
            household_id: profile.household_id,
            user_id: existingProfile.id,
            role: role,
            joined_at: new Date().toISOString()
          }
        ])
        .select()
        .single();

      if (memberError) {
        console.error('Error adding member to household:', memberError);
        return { data: null, error: memberError };
      }

      // Update user's profile to include household
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          household_id: profile.household_id,
          role: role,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingProfile.id);

      if (updateError) {
        console.error('Error updating user profile:', updateError);
        return { data: null, error: updateError };
      }

      return { data: { invited: true, existing_user: true }, error: null };
    } else {
      // For now, we'll return a message that the user needs to sign up first
      // In a real implementation, you might send an email invitation
      return { 
        data: { invited: true, existing_user: false }, 
        error: null 
      };
    }
  } catch (err) {
    console.error('Unexpected error inviting user:', err);
    return { 
      data: null, 
      error: { message: 'Er is een fout opgetreden bij het uitnodigen van de gebruiker.' } 
    };
  }
};

// Remove user from household
export const removeUserFromHousehold = async (userId: string) => {
  try {
    const { data: profile, error: profileError } = await getCurrentUserProfile();
    
    if (profileError || !profile?.household_id) {
      return { data: null, error: profileError || { message: 'Geen huishouden gevonden' } };
    }

    // Check if user is admin
    if (!profile.is_admin) {
      return { data: null, error: { message: 'Alleen beheerders kunnen gebruikers verwijderen' } };
    }

    // Can't remove yourself
    if (userId === profile.id) {
      return { data: null, error: { message: 'Je kunt jezelf niet uit het huishouden verwijderen' } };
    }

    // Remove from household_members
    const { error: memberError } = await supabase
      .from('household_members')
      .delete()
      .eq('household_id', profile.household_id)
      .eq('user_id', userId);

    if (memberError) {
      console.error('Error removing member from household:', memberError);
      return { data: null, error: memberError };
    }

    // Update user's profile to remove household
    const { error: updateError } = await supabase
      .from('profiles')
      .update({
        household_id: null,
        role: null,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);

    if (updateError) {
      console.error('Error updating user profile:', updateError);
      return { data: null, error: updateError };
    }

    return { data: { removed: true }, error: null };
  } catch (err) {
    console.error('Unexpected error removing user:', err);
    return { 
      data: null, 
      error: { message: 'Er is een fout opgetreden bij het verwijderen van de gebruiker.' } 
    };
  }
};

// Update user role in household
export const updateUserRole = async (userId: string, role: 'admin' | 'adult' | 'child') => {
  try {
    const { data: profile, error: profileError } = await getCurrentUserProfile();
    
    if (profileError || !profile?.household_id) {
      return { data: null, error: profileError || { message: 'Geen huishouden gevonden' } };
    }

    // Check if user is admin
    if (!profile.is_admin) {
      return { data: null, error: { message: 'Alleen beheerders kunnen rollen wijzigen' } };
    }

    // Update household_members role
    const { error: memberError } = await supabase
      .from('household_members')
      .update({ role: role })
      .eq('household_id', profile.household_id)
      .eq('user_id', userId);

    if (memberError) {
      console.error('Error updating member role:', memberError);
      return { data: null, error: memberError };
    }

    // Update user's profile role and admin status
    const { error: updateError } = await supabase
      .from('profiles')
      .update({
        role: role,
        is_admin: role === 'admin',
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);

    if (updateError) {
      console.error('Error updating user profile role:', updateError);
      return { data: null, error: updateError };
    }

    return { data: { updated: true }, error: null };
  } catch (err) {
    console.error('Unexpected error updating user role:', err);
    return { 
      data: null, 
      error: { message: 'Er is een fout opgetreden bij het bijwerken van de gebruikersrol.' } 
    };
  }
};

export const uploadDocument = async (file: File, userId: string, householdId: string) => {
  try {
    // Create secure file path: userId/timestamp-sanitized-filename
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const timestamp = Date.now();
    const fileName = `${userId}/${timestamp}-${sanitizedFileName}`;
    
    debug.log(`Uploading document: ${fileName} to documents bucket`);

    // Upload file to Supabase Storage
    const { data, error } = await supabase.storage
      .from('documents')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
        duplex: 'half' // Required for streaming
      });
    
    if (error) {
      console.error('Upload error:', error);
      return { data: null, error };
    }

    debug.log('File uploaded successfully:', data.path);

    // Create document record in database
    const { data: documentRecord, error: dbError } = await supabase
      .from('document')
      .insert([
        {
          user_id: userId,
          household_id: householdId,
          family: 'GENERAL', // Default family, will be updated by AI classification
          sub_type: 'uploaded',
          file_path: data.path,
          type: file.type,
          status: 'new',
          created_at: new Date().toISOString()
        }
      ])
      .select()
      .single();

    if (dbError) {
      console.error('Database record creation error:', dbError);
      // Clean up uploaded file if database insert fails
      await supabase.storage.from('documents').remove([data.path]);
      return { data: null, error: dbError };
    }

    debug.log('Document record created:', documentRecord.id);

    return { 
      data: { 
        ...data, 
        documentId: documentRecord.id,
        documentRecord 
      }, 
      error: null 
    };
  } catch (err) {
    console.error('Unexpected upload error:', err);
    return { 
      data: null, 
      error: { message: 'Er is een onverwachte fout opgetreden bij het uploaden.' } 
    };
  }
};

// Get signed URL for a document (for viewing/downloading)
export const getDocumentSignedUrl = async (storagePath: string, expiresIn = 3600) => {
  try {
    const { data, error } = await supabase.storage
      .from('documents')
      .createSignedUrl(storagePath, expiresIn);

    if (error) {
      console.error('Signed URL generation error:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (err) {
    console.error('Unexpected signed URL error:', err);
    return { 
      data: null, 
      error: { message: 'Er is een fout opgetreden bij het ophalen van de document URL.' } 
    };
  }
};

// Delete document (both storage and database record)
export const deleteDocument = async (documentId: string) => {
  try {
    // First get the document record to find storage path
    const { data: document, error: fetchError } = await supabase
      .from('document')
      .select('file_path, user_id')
      .eq('id', documentId)
      .single();

    if (fetchError || !document) {
      return { data: null, error: fetchError || { message: 'Document niet gevonden' } };
    }

    // Check if current user owns this document
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || user.id !== document.user_id) {
      return { data: null, error: { message: 'Niet geautoriseerd om dit document te verwijderen' } };
    }

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from('documents')
      .remove([document.file_path]);

    if (storageError) {
      console.error('Storage deletion error:', storageError);
    }

    // Delete database record
    const { error: dbError } = await supabase
      .from('document')
      .delete()
      .eq('id', documentId);

    if (dbError) {
      console.error('Database deletion error:', dbError);
      return { data: null, error: dbError };
    }

    return { data: { deleted: true }, error: null };
  } catch (err) {
    console.error('Unexpected deletion error:', err);
    return { 
      data: null, 
      error: { message: 'Er is een fout opgetreden bij het verwijderen van het document.' } 
    };
  }
};

// Get documents for current user's household
export const getUserDocuments = async () => {
  try {
    const { data: profile, error: profileError } = await getCurrentUserProfile();
    
    if (profileError || !profile) {
      return { data: null, error: profileError || { message: 'Profiel niet gevonden' } };
    }

    const { data: documents, error } = await supabase
      .from('document')
      .select(`
        *,
        profiles:user_id (
          first_name,
          last_name,
          email
        )
      `)
      .eq('household_id', profile.household_id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching documents:', error);
      return { data: null, error };
    }

    return { data: documents, error: null };
  } catch (err) {
    console.error('Unexpected error fetching documents:', err);
    return { 
      data: null, 
      error: { message: 'Er is een fout opgetreden bij het ophalen van documenten.' } 
    };
  }
};

// Get current user profile with type safety
export const getCurrentUserProfile = async () => {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError || !user) {
    console.error('Error getting user:', userError);
    return { data: null, error: userError };
  }

  // First try to get existing profile
  const { data: profile, error } = await supabase
    .from('profiles')
    .select(`
      *,
      households (
        id,
        name,
        created_at,
        updated_at
      )
    `)
    .eq('id', user.id)
    .single();
  
  // If profile doesn't exist (common with OAuth), create it
  if (error && error.code === 'PGRST116') {
            debug.log('Profile not found, creating new profile for OAuth user');
    return await createProfileForUser(user);
  }
  
  if (error) {
    console.error('Error getting profile:', error);
    return { data: null, error };
  }
  
  return { data: profile, error };
};

// Helper function to create profile and default household for OAuth users
const createProfileForUser = async (user: Record<string, unknown>) => {
  try {
    // Check if profile already exists to prevent race conditions
    const { data: existingProfile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (existingProfile) {
      debug.log('Profile already exists, returning existing profile');
      return { data: existingProfile, error: null };
    }

    // Create default household first
    const { data: household, error: householdError } = await supabase
      .from('households')
      .insert([
        {
          name: `${user.user_metadata?.full_name || user.email}'s Household`,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      ])
      .select()
      .single();

    if (householdError) {
      // Log without sensitive details
      console.error('Household creation error:', { 
        code: householdError.code, 
        message: householdError.message 
      });
      return { data: null, error: householdError };
    }

    // Create profile with household reference
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .insert([
        {
          id: user.id,
          email: user.email,
          first_name: user.user_metadata?.given_name || null,
          last_name: user.user_metadata?.family_name || null,
          avatar_url: user.user_metadata?.avatar_url || null,
          household_id: household.id,
          is_admin: true, // First user in household is admin
          role: 'admin',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      ])
      .select(`
        *,
        households (
          id,
          name,
          created_at,
          updated_at
        )
      `)
      .single();

    if (profileError) {
      // Check if it's a race condition (profile created by another request)
      if (profileError.code === '23505') { // unique_violation
        debug.log('Profile creation race condition detected, fetching existing profile');
        const { data: existingProfile, error: fetchError } = await supabase
          .from('profiles')
          .select(`
            *,
            households (
              id,
              name,
              created_at,
              updated_at
            )
          `)
          .eq('id', user.id)
          .single();

        if (fetchError) {
          console.error('Error fetching existing profile:', { 
            code: fetchError.code, 
            message: fetchError.message 
          });
          return { data: null, error: fetchError };
        }

        return { data: existingProfile, error: null };
      }

      // Log without sensitive details
      console.error('Profile creation error:', { 
        code: profileError.code, 
        message: profileError.message 
      });
      return { data: null, error: profileError };
    }

    // Create household_members entry
    const { error: memberError } = await supabase
      .from('household_members')
      .insert([
        {
          household_id: household.id,
          user_id: user.id,
          role: 'admin',
          joined_at: new Date().toISOString()
        }
      ]);

    if (memberError) {
      console.error('Household member creation error:', { 
        code: memberError.code, 
        message: memberError.message 
      });
      // Don't fail the whole process for this
    }

    debug.log('Successfully created profile and household for OAuth user');
    return { data: profile, error: null };
  } catch (err) {
    console.error('Unexpected error creating profile:', err);
    return { 
      data: null, 
      error: { message: 'Er is een onverwachte fout opgetreden bij het aanmaken van het profiel.' } 
    };
  }
};

// ===== ACTION ITEMS & TIPS MANAGEMENT =====

/**
 * Get all action items for a household
 */
export const getUserActionItems = async () => {
  try {
    const { data: profile, error: profileError } = await getCurrentUserProfile();
    
    if (profileError || !profile?.household_id) {
      return { data: null, error: profileError || { message: 'Geen huishouden gevonden' } };
    }

    const { data: actionItems, error } = await supabase
      .from('action_item')
      .select(`
        *,
        document:doc_id (
          id,
          family,
          file_path
        )
      `)
      .eq('household_id', profile.household_id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching action items:', error);
      return { data: null, error };
    }

    return { data: actionItems, error: null };
  } catch (err) {
    console.error('Unexpected error fetching action items:', err);
    return { 
      data: null, 
      error: { message: 'Er is een fout opgetreden bij het ophalen van actie-items.' } 
    };
  }
};

/**
 * Update action item status
 */
export const updateActionItemStatus = async (actionId: string, status: 'new' | 'in_progress' | 'done' | 'snoozed' | 'error') => {
  try {
    const { data, error } = await supabase
      .from('action_item')
      .update({ 
        status: status,
        completed_at: status === 'done' ? new Date().toISOString() : null
      })
      .eq('action_id', actionId)
      .select()
      .single();

    if (error) {
      console.error('Error updating action item status:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (err) {
    console.error('Unexpected error updating action item:', err);
    return { 
      data: null, 
      error: { message: 'Er is een fout opgetreden bij het bijwerken van het actie-item.' } 
    };
  }
};

/**
 * Generate and get tips based on user's documents and action items
 */
export const getUserTips = async () => {
  try {
    const { data: profile, error: profileError } = await getCurrentUserProfile();
    
    if (profileError || !profile?.household_id) {
      return { data: null, error: profileError || { message: 'Geen huishouden gevonden' } };
    }

    // Fetch tips from database
    const { data: tips, error: tipsError } = await supabase
      .from('tips')
      .select('*')
      .eq('household_id', profile.household_id)
      .order('relevance_score', { ascending: false })
      .order('created_at', { ascending: false });

    if (tipsError) {
      console.error('Error fetching tips:', tipsError);
      return { data: null, error: tipsError };
    }

    // If no tips exist, generate some basic ones
    if (!tips || tips.length === 0) {
      // Check if user has any documents
      const { data: documents } = await supabase
        .from('document')
        .select('id')
        .eq('household_id', profile.household_id)
        .limit(1);

      if (!documents || documents.length === 0) {
        // Return a single tip encouraging document upload
        return {
          data: [{
            id: 'default-tip-1',
            title: 'Start met documenten uploaden',
            content: 'Upload je belangrijkste financiële documenten zoals salarisstroken, contracten en belastingpapieren om gepersonaliseerde inzichten en bespaartips te krijgen.',
            category: 'Algemeen',
            relevance_score: 100,
            household_id: profile.household_id
          }],
          error: null
        };
      }
    }

    return { data: tips || [], error: null };
  } catch (err) {
    console.error('Unexpected error fetching tips:', err);
    return { 
      data: null, 
      error: { message: 'Er is een fout opgetreden bij het ophalen van tips.' } 
    };
  }
};

